import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FiRefreshCw,
  FiFilter,
  FiUsers,
  FiTarget,
  FiBarChart2,
  FiDatabase,
  FiPackage,
  FiMapPin
} from 'react-icons/fi';
import { filterDataService } from '../../services/api';

const labelPt = {
  departamentos: 'Departamentos',
  atividades: 'Atividades',
  marcas: 'Marcas',
  filiais: 'Filiais',
  produtos: 'Produtos',
  vendedores: 'Vendedores (RCA)',
  'sales-reports-sync': 'Sincronização de Relatórios'
};

function StatCard({ icon: Icon, title, value, color, isLoading }) {
  return (
    <div className="stat-card-modern">
      <div className={`stat-icon-container bg-${color}-light text-${color}`}>
        <Icon size={24} />
      </div>
      <div className="stat-content">
        <div className="stat-label-modern">{title}</div>
        <div className="stat-value-modern">
          {isLoading ? (
            <div className="spinner-border spinner-border-sm text-muted" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
          ) : (
            value
          )}
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await filterDataService.getSyncStatus();
      setSyncStatus(res?.data?.sync_status || null);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Erro ao carregar status');
      setSyncStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filiais = syncStatus?.filiais?.total ?? '—';
  const produtos = syncStatus?.produtos?.total ?? '—';
  const vendedores = syncStatus?.vendedores?.total ?? '—';
  const departamentos = syncStatus?.departamentos?.total ?? '—';

  return (
    <div className="container-fluid py-4">
      <div className="mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h2 className="mb-1">Dashboard</h2>
          <p className="text-muted mb-0">Visão geral dos dados sincronizados para filtros e relatórios de faturamento.</p>
        </div>
        <button type="button" className="btn-modern btn-modern-light" onClick={load} title="Atualizar">
          <FiRefreshCw size={16} className={loading ? 'spin-ico' : ''} /> Atualizar
        </button>
      </div>



      <div className="stats-grid-modern mb-4">
        <StatCard icon={FiMapPin} title="Filiais sincronizadas" value={filiais} color="primary" isLoading={loading} />
        <StatCard icon={FiPackage} title="Produtos" value={produtos} color="success" isLoading={loading} />
        <StatCard icon={FiUsers} title="Vendedores (RCA)" value={vendedores} color="info" isLoading={loading} />
        <StatCard icon={FiDatabase} title="Departamentos" value={departamentos} color="warning" isLoading={loading} />
      </div>

      <div className="row g-4 mb-4">
        <div className="col-md-4">
          <Link to="/admin/vendedores" className="text-decoration-none text-reset d-block">
            <div className="stat-card-modern h-100">
              <div className="stat-icon-container bg-primary-light text-primary">
                <FiUsers size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-label-modern">Cadastro</div>
                <div className="stat-value-modern" style={{ fontSize: '1.1rem' }}>
                  Vendedores
                </div>
                <small className="text-muted">Lista e RCA sincronizados</small>
              </div>
            </div>
          </Link>
        </div>
        <div className="col-md-4">
          <Link to="/admin/metas" className="text-decoration-none text-reset d-block">
            <div className="stat-card-modern h-100">
              <div className="stat-icon-container bg-success-light text-success">
                <FiTarget size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-label-modern">Planejamento</div>
                <div className="stat-value-modern" style={{ fontSize: '1.1rem' }}>
                  Metas
                </div>
                <small className="text-muted">Valor por vendedor e período</small>
              </div>
            </div>
          </Link>
        </div>
        <div className="col-md-4">
          <Link to="/admin/filtros" className="text-decoration-none text-reset d-block">
            <div className="stat-card-modern h-100">
              <div className="stat-icon-container bg-warning-light text-warning">
                <FiFilter size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-label-modern">Relatórios</div>
                <div className="stat-value-modern" style={{ fontSize: '1.1rem' }}>
                  Filtros salvos
                </div>
                <small className="text-muted">Conjuntos para consultas</small>
              </div>
            </div>
          </Link>
        </div>
      </div>

      <div className="card-modern shadow-sm-modern">
        <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-0">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <FiBarChart2 />
              Detalhe da sincronização (MySQL)
            </h5>
          </div>
        </div>
        <div className="card-body-modern p-4">
          {error && <div className="alert alert-warning py-2 small mb-3">{error}</div>}
          {loading && !syncStatus ? (
            <p className="text-muted small mb-0">Carregando…</p>
          ) : syncStatus ? (
            <div className="table-responsive">
              <table className="table-modern w-100">
                <thead>
                  <tr>
                    <th>Origem</th>
                    <th>Registros</th>
                    <th>Última atualização</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(syncStatus).map(([key, val]) => (
                    <tr key={key}>
                      <td className="fw-medium">{labelPt[key] || key}</td>
                      <td>{val.total?.toLocaleString?.('pt-BR') ?? val.total}</td>
                      <td>
                        <small className="text-muted">
                          {val.ultima_atualizacao
                            ? new Date(val.ultima_atualizacao).toLocaleString('pt-BR')
                            : '—'}
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted small mb-0">Nenhum dado de sincronização.</p>
          )}
        </div>
      </div>

      <style>{`
        .spin-ico { animation: spin-ico 0.8s linear infinite; display: inline-block; }
        @keyframes spin-ico { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default Dashboard;
